export interface CommandAgentLlmModel {
    provider: string;
    model_key: string;
    model_id: string;
    display_name: string;
    default_max_tokens: number;
    default_temperature: number;
    temperature_min?: number;
    temperature_max?: number;
    max_tokens_min?: number;
    max_tokens_max?: number;
}

const PROVIDER_LABELS: Record<string, string> = {
    OPENAI: "OpenAI",
    ANTHROPIC: "Anthropic",
    MOONSHOTAI: "MoonshotAI",
    ZHIPU: "Zhipu",
    DEEPSEEK: "DeepSeek",
    META: "Meta",
};

/** Models that must appear in the composer picker even if the API payload is stale. */
const QUICK_SELECTOR_FALLBACK_MODELS: CommandAgentLlmModel[] = [
    {
        provider: "ZHIPU",
        model_key: "GLM-5.2",
        model_id: "GLM-5.2",
        display_name: "GLM 5.2",
        default_max_tokens: 128000,
        default_temperature: 0.7,
    },
    {
        provider: "ZHIPU",
        model_key: "GLM-5.3",
        model_id: "GLM-5.3",
        display_name: "GLM-5.3",
        default_max_tokens: 128000,
        default_temperature: 0.7,
    },
    {
        provider: "ZHIPU",
        model_key: "GLM-5.3-FLASH",
        model_id: "GLM-5.3-FLASH",
        display_name: "GLM-5.3-Flash",
        default_max_tokens: 128000,
        default_temperature: 0.7,
    },
    {
        provider: "MOONSHOTAI",
        model_key: "KIMI-K2.6",
        model_id: "KIMI-K2.6",
        display_name: "Kimi K2.6",
        default_max_tokens: 262144,
        default_temperature: 1,
    },
    {
        provider: "DEEPSEEK",
        model_key: "DEEPSEEK-V4.1-FLASH",
        model_id: "DEEPSEEK-V4.1-FLASH",
        display_name: "DeepSeek V4.1 Flash",
        default_max_tokens: 128000,
        default_temperature: 0.7,
    },
];

export function readLlmModelsResponse(data: unknown): CommandAgentLlmModel[] {
    if (!data) return [];
    if (Array.isArray(data)) return data as CommandAgentLlmModel[];
    if (typeof data !== "object") return [];
    const record = data as Record<string, unknown>;
    const nested = record.models ?? record.items;
    if (Array.isArray(nested)) return nested as CommandAgentLlmModel[];
    if (record.data && typeof record.data === "object") {
        const inner = record.data as Record<string, unknown>;
        const innerModels = inner.models ?? inner.items;
        if (Array.isArray(innerModels)) return innerModels as CommandAgentLlmModel[];
    }
    return [];
}

export function getCommandQuickSelectorModels(data: unknown): CommandAgentLlmModel[] {
    const fromApi = readLlmModelsResponse(data);
    const seen = new Set(
        fromApi.flatMap((model) => {
            const provider = normalizeLlmProvider(model.provider);
            return [
                `${provider}:${(model.model_key ?? "").toUpperCase()}`,
                `${provider}:${(model.model_id ?? "").toUpperCase()}`,
            ];
        })
    );
    const extras = QUICK_SELECTOR_FALLBACK_MODELS.filter((model) => {
        const provider = normalizeLlmProvider(model.provider);
        const key = `${provider}:${model.model_key.toUpperCase()}`;
        const altKey = `${provider}:${model.model_id.toUpperCase()}`;
        return !seen.has(key) && !seen.has(altKey);
    });
    return [...fromApi, ...extras];
}

export function groupLlmModelsByProvider(models: CommandAgentLlmModel[]) {
    const groups = new Map<string, CommandAgentLlmModel[]>();
    for (const model of models) {
        const provider = normalizeLlmProvider(model.provider) || model.provider || "OTHER";
        const existing = groups.get(provider) ?? [];
        existing.push(model);
        groups.set(provider, existing);
    }
    return [...groups.entries()].sort(([a], [b]) =>
        getLlmProviderLabel(a).localeCompare(getLlmProviderLabel(b))
    );
}

export function normalizeLlmProvider(provider: string | undefined | null): string {
    return (provider ?? "").trim().toUpperCase();
}

export function getLlmProviderLabel(provider: string): string {
    const normalized = normalizeLlmProvider(provider);
    return PROVIDER_LABELS[normalized] ?? provider;
}

export function getProviderOptionsFromModels(models: CommandAgentLlmModel[] | undefined) {
    if (!models?.length) {
        return Object.entries(PROVIDER_LABELS).map(([value, label]) => ({ value, label }));
    }

    const seen = new Set<string>();
    const options: Array<{ value: string; label: string }> = [];

    for (const model of models) {
        const value = normalizeLlmProvider(model.provider);
        if (!value || seen.has(value)) continue;
        seen.add(value);
        options.push({ value, label: getLlmProviderLabel(value) });
    }

    return options.sort((a, b) => a.label.localeCompare(b.label));
}

export function getModelsForProvider(
    models: CommandAgentLlmModel[] | undefined,
    provider: string
) {
    if (!models?.length || !provider) return [];
    const normalized = normalizeLlmProvider(provider);
    return models.filter((model) => normalizeLlmProvider(model.provider) === normalized);
}

export function findLlmModel(
    models: CommandAgentLlmModel[] | undefined,
    modelRef: string | undefined | null
) {
    if (!models?.length || !modelRef) return null;
    return (
        models.find(
            (model) =>
                model.model_key === modelRef ||
                model.model_id === modelRef ||
                model.display_name === modelRef
        ) ?? null
    );
}

export function getDefaultModelForProvider(
    models: CommandAgentLlmModel[] | undefined,
    provider: string
) {
    return getModelsForProvider(models, provider)[0] ?? null;
}

export function resolveLlmFormState(
    models: CommandAgentLlmModel[] | undefined,
    options: {
        provider?: string | null;
        model?: string | null;
        temperature?: number | null;
        maxTokens?: number | null;
    }
) {
    const matched = findLlmModel(models, options.model ?? undefined);
    const provider =
        options.provider ||
        matched?.provider ||
        getDefaultModelForProvider(models, "OPENAI")?.provider ||
        "OPENAI";
    const providerModels = getModelsForProvider(models, provider);
    const modelInProvider =
        matched && providerModels.some((item) => item.model_key === matched.model_key)
            ? matched
            : null;
    const selected = modelInProvider ?? providerModels[0] ?? matched;

    return {
        llmProvider: normalizeLlmProvider(provider) || provider,
        model: selected?.model_key ?? options.model ?? "",
        temperature: String(
            options.temperature ?? selected?.default_temperature ?? 0.2
        ),
        maxTokens: String(options.maxTokens ?? selected?.default_max_tokens ?? 8192),
        selectedModel: selected,
    };
}

export function applyLlmProviderChange<
    T extends {
        llm_provider: string;
        llm_model: string;
    },
>(prev: T, provider: string, models: CommandAgentLlmModel[] | undefined): T {
    const firstModel = getDefaultModelForProvider(models, provider);

    const next: T = {
        ...prev,
        llm_provider: normalizeLlmProvider(provider) || provider,
        llm_model: firstModel?.model_key ?? "",
    };

    if ("llm_max_tokens" in prev && typeof prev.llm_max_tokens === "string") {
        (next as T & { llm_max_tokens: string }).llm_max_tokens = firstModel
            ? String(firstModel.default_max_tokens)
            : "";
    }

    if ("llm_temperature" in prev && typeof prev.llm_temperature === "string") {
        (next as T & { llm_temperature: string }).llm_temperature = firstModel
            ? String(firstModel.default_temperature)
            : "";
    }

    return next;
}

export function isModelValidForProvider(
    models: CommandAgentLlmModel[] | undefined,
    provider: string,
    modelRef: string | undefined | null
) {
    if (!modelRef) return false;
    return getModelsForProvider(models, provider).some(
        (item) => item.model_key === modelRef || item.model_id === modelRef
    );
}
